'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { supabase } from '../_utils/supabase'

export default function ProfilePicture({
    username,
    size = 128,
    className = '',
}) {
    const [avatarUrl, setAvatarUrl] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        async function fetchProfilePicture() {
            try {
                // Get the current user's session
                const { data: { session } } = await supabase.auth.getSession()

                // If we're viewing our own profile, use the session user metadata
                if (session?.user?.user_metadata?.username === username) {
                    if (session.user.user_metadata.custom_avatar_url) {
                        setAvatarUrl(session.user.user_metadata.custom_avatar_url)
                    } else {
                        setAvatarUrl(session.user.user_metadata.avatar_url)
                    }
                } else {
                    // For other users, get their profile data from our API endpoint
                    const response = await fetch(`/api/user/${username}`)
                    const data = await response.json()

                    if (!response.ok) {
                        throw new Error(data.error || 'Failed to fetch user data')
                    }

                    // Check for custom_avatar_url first, then fall back to avatar_url
                    if (data.custom_avatar_url) {
                        setAvatarUrl(data.custom_avatar_url)
                    } else if (data.avatar_url) {
                        setAvatarUrl(data.avatar_url)
                    }
                }
            } catch (err) {
                console.error('Error fetching profile picture:', err)
                setError(err)
            } finally {
                setLoading(false)
            }
        }

        if (username) {
            fetchProfilePicture()
        }
    }, [username])

    if (loading) {
        return (
            <div
                className={`bg-gray-200 rounded-full aspect-square `}
                style={size ? { width: size, height: size } : undefined}
            />
        )
    }

    if (avatarUrl) {
        return (
            <div 
                className={`relative overflow-hidden rounded-full `} 
                style={{ 
                    width: size,
                    height: size,
                    minWidth: size,
                    minHeight: size,
                    maxWidth: size,
                    maxHeight: size
                }}
            >
                <Image
                    src={avatarUrl}
                    alt={`${username}'s profile picture`}
                    fill
                    sizes={`${size}px`}
                    className="object-cover test"
                    style={{
                        width: '100%',
                        height: '100%'
                    }}
                    priority
                />
            </div>
        )
    }

    // Default to first letter in circle
    return (
        <div
            className={`bg-gray-200 rounded-full aspect-square flex items-center justify-center `}
            style={size ? { width: size, height: size } : undefined}
        >
            <span 
                className="text-gray-500" 
                style={{ fontSize: size ? size * 0.4 : '2rem' }}
            >
                {username?.[0]?.toUpperCase() || '?'}
            </span>
        </div>
    )
}
